import { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Trophy, Users, Swords, ChevronRight, Check, X, AlertCircle, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { getDefaultLeagueId } from "@workspace/profile-completion";
import { PlayerSearchInput } from "./PlayerSearchInput";
import { ClubPicker } from "./ClubPicker";

interface SelectedPlayer {
  id?: string;
  name: string;
  email?: string;
  isInvite?: boolean;
}

interface LeagueMember {
  id: string;
  name: string;
  rating: number;
}

interface MatchRecordFormProps {
  leagues?: { id: string; name: string; homeClubId?: string | null; isFavorite?: boolean | null }[];
  currentUserId?: string;
  currentUserName?: string;
  onLeagueChange?: (leagueId: string) => Promise<LeagueMember[]>;
  onSubmit?: (data: MatchData) => void;
  fixedLeagueId?: string;
  isAdminMode?: boolean;
  fixedLeagueMembers?: LeagueMember[];
  matchFormat?: "singles" | "doubles";
  /**
   * homeClubId on the (selected/fixed) league, used as the first
   * source for the auto-fill chain when the user has not yet
   * picked a club for the match.
   */
  leagueDefaultHomeClubId?: string | null;
  /** homeClubId on the current user's profile (second auto-fill source). */
  profileHomeClubId?: string | null;
  /** Country (ISO-2) used to scope the ClubPicker search. */
  country?: string | null;
}

interface MatchData {
  team1Player1?: string;
  partner: string;
  opponent1: string;
  opponent2: string;
  score: { myTeam: number; opponents: number }[];
  competitive: boolean;
  leagueIds: string[];
  homeClubId?: string | null;
}

const COMMON_SCORES = [0, 1, 2, 3, 4, 5, 6, 7];
const QUICK_RESULTS: ReadonlyArray<{ myTeam: number; opponents: number }> = [
  { myTeam: 6, opponents: 0 },
  { myTeam: 6, opponents: 2 },
  { myTeam: 6, opponents: 4 },
  { myTeam: 7, opponents: 5 },
  { myTeam: 7, opponents: 6 },
];

function autoFillOther(picked: number, currentOther: number | null): number | null {
  if (currentOther !== null && currentOther !== 0) return currentOther;
  if (picked >= 0 && picked <= 4) return 6;
  if (picked === 5) return 7;
  if (picked === 7) return 5;
  return currentOther;
}

function SearchableMemberSelect({
  value,
  onValueChange,
  members,
  disabled = false,
  placeholder,
  colorScheme,
  testId,
  loadingText,
}: {
  value: string;
  onValueChange: (id: string) => void;
  members: LeagueMember[];
  disabled?: boolean;
  placeholder: string;
  colorScheme: "primary" | "destructive";
  testId: string;
  loadingText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const getInitials = (name: string) =>
    name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";

  const selectedMember = members.find(m => m.id === value);

  const styles = colorScheme === "primary"
    ? {
        borderActive: "border-primary/50",
        borderHover: "hover:border-primary/50",
        bgActive: "bg-primary/20",
        textActive: "text-primary",
        bgItem: "bg-primary/10",
        textItem: "text-primary",
        bgSelected: "bg-primary/10",
      }
    : {
        borderActive: "border-destructive/50",
        borderHover: "hover:border-destructive/50",
        bgActive: "bg-destructive/20",
        textActive: "text-destructive",
        bgItem: "bg-destructive/10",
        textItem: "text-destructive",
        bgSelected: "bg-destructive/10",
      };

  const filtered = members
    .filter(m => m.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <div ref={containerRef} className="relative w-full" data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen(prev => !prev); }}
        className={cn(
          "w-full flex flex-col items-center p-3 rounded-xl border-2 border-dashed transition-all",
          value
            ? cn(styles.borderActive, "bg-background/50")
            : cn("border-muted-foreground/30", styles.borderHover),
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <Avatar className="h-12 w-12 mb-2 border-2 border-muted">
          <AvatarFallback className={cn(
            "text-sm font-bold",
            value ? cn(styles.bgActive, styles.textActive) : "bg-muted text-muted-foreground"
          )}>
            {selectedMember ? getInitials(selectedMember.name) : <Plus className="h-5 w-5" />}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium truncate w-full text-center">
          {selectedMember ? selectedMember.name : placeholder}
        </span>
        <span className="text-xs text-muted-foreground">
          {loadingText || "Toca para elegir"}
        </span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl border bg-popover shadow-lg">
          <div className="flex items-center gap-2 p-2 border-b">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-[250px] overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map(member => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => {
                    onValueChange(member.id);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover-elevate",
                    member.id === value && styles.bgSelected
                  )}
                  data-testid={`${testId}-option-${member.id}`}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className={cn(styles.bgItem, styles.textItem, "text-xs")}>
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium text-sm">{member.name}</div>
                    <div className="text-xs text-muted-foreground">Rating: {member.rating}</div>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                {search ? "Sin resultados" : "Sin jugadores disponibles"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function MatchRecordForm({ 
  leagues = [], 
  currentUserId,
  currentUserName = "Tú",
  onLeagueChange,
  onSubmit,
  fixedLeagueId,
  isAdminMode = false,
  fixedLeagueMembers = [],
  matchFormat = "doubles",
  leagueDefaultHomeClubId = null,
  profileHomeClubId = null,
  country = null,
}: MatchRecordFormProps) {
  // League match player IDs
  const [team1Player1, setTeam1Player1] = useState("");
  const [partner, setPartner] = useState("");
  const [opponent1, setOpponent1] = useState("");
  const [opponent2, setOpponent2] = useState("");
  
  // Non-league match selected players
  const [partnerPlayer, setPartnerPlayer] = useState<SelectedPlayer | null>(null);
  const [opponent1Player, setOpponent1Player] = useState<SelectedPlayer | null>(null);
  const [opponent2Player, setOpponent2Player] = useState<SelectedPlayer | null>(null);
  
  const [score, setScore] = useState<{ myTeam: number | null, opponents: number | null }[]>([{ myTeam: null, opponents: null }]);
  const [competitive, setCompetitive] = useState(true);
  const [selectedLeagues, setSelectedLeagues] = useState<string[]>([]);
  // homeClubId for this match. Auto-fill chain:
  //   1. selected/fixed league.homeClubId
  //   2. profile.homeClubId
  //   3. empty
  // Once the user picks (or clears) a club explicitly we stop
  // auto-overwriting it, tracked via `clubTouched`.
  const [homeClubId, setHomeClubId] = useState<string | null>(null);
  const [clubTouched, setClubTouched] = useState(false);
  const [leagueMembersMap, setLeagueMembersMap] = useState<Record<string, LeagueMember[]>>({});
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Force the "Partido Competitivo" toggle back to checked every
  // time this form is mounted. The host pages (RecordMatchPage and
  // any other entry point) are expected to give the form a fresh
  // mount on each opening — this effect is the explicit, defensive
  // contract that documents and enforces "the toggle always starts
  // checked when the user enters the form, regardless of whatever
  // value the previous form session ended on".
  useEffect(() => {
    setCompetitive(true);
  }, []);
  
  // Use ref to store the callback to avoid infinite loops
  const onLeagueChangeRef = useRef(onLeagueChange);
  onLeagueChangeRef.current = onLeagueChange;

  // Load members for all selected leagues (skip when using fixed league with pre-loaded members)
  useEffect(() => {
    // Skip member loading if we have a fixed league with pre-loaded members
    if (fixedLeagueId && fixedLeagueMembers.length > 0) {
      return;
    }
    
    let cancelled = false;
    
    const loadMembers = async () => {
      const callback = onLeagueChangeRef.current;
      if (selectedLeagues.length === 0 || !callback) {
        setLeagueMembersMap({});
        setLoadingMembers(false);
        return;
      }

      setLoadingMembers(true);
      const newMap: Record<string, LeagueMember[]> = {};
      
      for (const leagueId of selectedLeagues) {
        if (cancelled) return;
        try {
          const members = await callback(leagueId);
          // Ensure members is an array
          newMap[leagueId] = Array.isArray(members) ? members : [];
        } catch (error) {
          console.error(`Error loading members for league ${leagueId}:`, error);
          newMap[leagueId] = [];
        }
      }
      
      if (!cancelled) {
        setLeagueMembersMap(newMap);
        setLoadingMembers(false);
      }
    };

    loadMembers();
    
    return () => {
      cancelled = true;
    };
  }, [selectedLeagues.join(','), fixedLeagueId, fixedLeagueMembers.length]);

  // Track if initial selection has been done (for fixed league)
  const initializedFixedLeague = useRef(false);

  // Reset player selections when leagues change (but never for fixed league mode)
  useEffect(() => {
    // Never reset in fixed league mode
    if (fixedLeagueId) {
      return;
    }
    setTeam1Player1("");
    setPartner("");
    setOpponent1("");
    setOpponent2("");
  }, [selectedLeagues.length, fixedLeagueId]);
  
  // Auto-select fixed league when provided (or when members are ready)
  useEffect(() => {
    if (fixedLeagueId && fixedLeagueMembers.length > 0 && !initializedFixedLeague.current) {
      initializedFixedLeague.current = true;
      if (!selectedLeagues.includes(fixedLeagueId)) {
        setSelectedLeagues([fixedLeagueId]);
      }
    }
  }, [fixedLeagueId, fixedLeagueMembers.length]);

  // Task #496 fallback default: when the screen isn't opened with a
  // fixed league (deep link / league-detail CTA — that wins), auto-
  // select the user's single favorite league, or their single real
  // league if they have no (or several) favorites. Runs once the
  // `leagues` list has data; this is only an *initial* value, so once
  // applied we don't touch `selectedLeagues` again even if `leagues`
  // is refetched later with different data.
  const defaultLeagueAppliedRef = useRef(false);
  useEffect(() => {
    if (fixedLeagueId) return;
    if (defaultLeagueAppliedRef.current) return;
    if (leagues.length === 0) return;
    defaultLeagueAppliedRef.current = true;
    const defaultLeagueId = getDefaultLeagueId(
      leagues.map((l) => ({ id: l.id, isFavorite: !!l.isFavorite })),
    );
    if (defaultLeagueId) {
      setSelectedLeagues([defaultLeagueId]);
    }
  }, [leagues, fixedLeagueId]);

  useEffect(() => {
    if (clubTouched) return;
    let fromSelected: string | null = null;
    if (!fixedLeagueId && selectedLeagues.length > 0) {
      const first = leagues.find((l) => l.id === selectedLeagues[0]);
      fromSelected = first?.homeClubId || null;
    }
    const fallback =
      leagueDefaultHomeClubId ||
      fromSelected ||
      profileHomeClubId ||
      null;
    setHomeClubId(fallback);
  }, [
    leagueDefaultHomeClubId,
    profileHomeClubId,
    selectedLeagues.join(','),
    fixedLeagueId,
    clubTouched,
    leagues,
  ]);

  const toggleLeague = (leagueId: string) => {
    setSelectedLeagues(prev => 
      prev.includes(leagueId) 
        ? prev.filter(id => id !== leagueId)
        : [...prev, leagueId]
    );
  };

  const addSet = () => {
    if (score.length < 3) {
      setScore([...score, { myTeam: null, opponents: null }]);
    }
  };

  const removeSet = (index: number) => {
    if (score.length > 1) {
      setScore(score.filter((_, i) => i !== index));
    }
  };

  const updateScore = (index: number, key: "myTeam" | "opponents", value: number) => {
    const newScore = [...score];
    const other: "myTeam" | "opponents" = key === "myTeam" ? "opponents" : "myTeam";
    newScore[index] = {
      ...newScore[index],
      [key]: value,
      [other]: autoFillOther(value, newScore[index][other]),
    };
    setScore(newScore);
  };

  const applyQuickResult = (index: number, myTeam: number, opponents: number) => {
    const newScore = [...score];
    newScore[index] = { myTeam, opponents };
    setScore(newScore);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // For fixed league mode, always use the fixed league ID
    const finalLeagueIds = fixedLeagueId ? [fixedLeagueId] : selectedLeagues;
    
    // For league matches, team1Player1 is the current user (unless admin mode where it can be selected)
    const finalTeam1Player1 = isAdminMode ? team1Player1 : currentUserId;
    
    onSubmit?.({
      team1Player1: finalTeam1Player1,
      partner: isLeagueMatch ? partner : (partnerPlayer?.id || partnerPlayer?.name || ""),
      opponent1: isLeagueMatch ? opponent1 : (opponent1Player?.id || opponent1Player?.name || ""),
      opponent2: isLeagueMatch ? opponent2 : (opponent2Player?.id || opponent2Player?.name || ""),
      score: score as { myTeam: number; opponents: number }[],
      competitive,
      leagueIds: finalLeagueIds,
      homeClubId,
    });
  };

  const myTeamWins = score.filter((s) => s.myTeam !== null && s.opponents !== null && s.myTeam > s.opponents).length;
  const opponentsWins = score.filter((s) => s.myTeam !== null && s.opponents !== null && s.opponents > s.myTeam).length;
  const matchWinner = (myTeamWins === 0 && opponentsWins === 0) ? null : (myTeamWins > opponentsWins ? "myTeam" : opponentsWins > myTeamWins ? "opponents" : null);

  // ---- Live result preview ---------------------------------------------
  // Mirrors the "Editar marcador" sheet (mobile + EditPendingMatchDialog)
  // so the editor sees the same immediate feedback (set tally + who's
  // currently winning) instead of just the plain "Ganador / Ganaste" line.
  // Real names are used when available; otherwise we fall back to
  // "Equipo 1 / Equipo 2".
  const allScoresEntered = score.every(
    (s) => s.myTeam !== null && s.opponents !== null,
  );
  const setsWithWinner = allScoresEntered
    ? score.filter((s) => (s.myTeam as number) !== (s.opponents as number)).length
    : 0;
  const allSetsResolved =
    allScoresEntered && setsWithWinner === score.length;

  // Get common members across all selected leagues
  const getCommonMembers = (): LeagueMember[] => {
    // If we have a fixed league with pre-loaded members, use them directly
    if (fixedLeagueId && fixedLeagueMembers.length > 0) {
      return fixedLeagueMembers;
    }
    
    if (selectedLeagues.length === 0) return [];
    
    const allMemberArrays = selectedLeagues.map(id => leagueMembersMap[id] || []);
    if (allMemberArrays.some(arr => arr.length === 0)) return [];
    
    // Find members that exist in ALL selected leagues
    const firstLeagueMembers = allMemberArrays[0];
    return firstLeagueMembers.filter(member => 
      allMemberArrays.every(arr => arr.some(m => m.id === member.id))
    );
  };

  const commonMembers = getCommonMembers();
  const isLeagueMatch = (selectedLeagues.length > 0 && commonMembers.length > 0) || 
                        (fixedLeagueId && fixedLeagueMembers.length > 0);
  
  // Filter members to ensure they have valid IDs (required for Select component)
  const validMembers = commonMembers.filter(m => m.id && typeof m.id === 'string' && m.id.length > 0);
  
  // In admin mode, all members are available including the current user (admin)
  // In regular mode, exclude current user from selections
  const availableTeam1Player1 = isAdminMode 
    ? validMembers.filter(m => m.id !== partner && m.id !== opponent1 && m.id !== opponent2)
    : [];
  const availablePartners = isAdminMode
    ? validMembers.filter(m => m.id !== team1Player1 && m.id !== opponent1 && m.id !== opponent2)
    : validMembers.filter(m => m.id !== currentUserId && m.id !== opponent1 && m.id !== opponent2);
  const availableOpponents1 = isAdminMode
    ? validMembers.filter(m => m.id !== team1Player1 && m.id !== partner && m.id !== opponent2)
    : validMembers.filter(m => m.id !== currentUserId && m.id !== partner && m.id !== opponent2);
  const availableOpponents2 = isAdminMode
    ? validMembers.filter(m => m.id !== team1Player1 && m.id !== partner && m.id !== opponent1)
    : validMembers.filter(m => m.id !== currentUserId && m.id !== partner && m.id !== opponent1);

  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
  };

  const getSelectedMemberName = (memberId: string) => {
    return commonMembers.find(m => m.id === memberId)?.name || "";
  };

  // Resolve the display names for each side so the live preview can use
  // real names ("Ana / Luis va ganando 2-1") instead of the generic
  // "Equipo 1 / Equipo 2" labels whenever the players are known.
  const team1DisplayName = useMemo(() => {
    const player1Name = isLeagueMatch
      ? isAdminMode
        ? getSelectedMemberName(team1Player1)
        : currentUserName
      : currentUserName;
    if (matchFormat === "singles") return player1Name || "Equipo 1";
    const partnerName = isLeagueMatch
      ? getSelectedMemberName(partner)
      : partnerPlayer?.name || "";
    return [player1Name, partnerName].filter(Boolean).join(" / ") || "Equipo 1";
  }, [
    isLeagueMatch,
    isAdminMode,
    team1Player1,
    currentUserName,
    matchFormat,
    partner,
    partnerPlayer,
    commonMembers,
  ]);

  const team2DisplayName = useMemo(() => {
    const opp1Name = isLeagueMatch
      ? getSelectedMemberName(opponent1)
      : opponent1Player?.name || "";
    if (matchFormat === "singles") return opp1Name || "Equipo 2";
    const opp2Name = isLeagueMatch
      ? getSelectedMemberName(opponent2)
      : opponent2Player?.name || "";
    return [opp1Name, opp2Name].filter(Boolean).join(" / ") || "Equipo 2";
  }, [
    isLeagueMatch,
    matchFormat,
    opponent1,
    opponent2,
    opponent1Player,
    opponent2Player,
    commonMembers,
  ]);

  const resultPreview = useMemo<{
    text: string;
    tone: "muted" | "team1" | "team2";
  }>(() => {
    if (!allScoresEntered) {
      return {
        text: "Ingresa los puntajes para ver el resultado",
        tone: "muted",
      };
    }
    if (setsWithWinner === 0) {
      return { text: "Sin definir aún", tone: "muted" };
    }
    if (myTeamWins === opponentsWins) {
      return { text: `Empate ${myTeamWins}-${opponentsWins}`, tone: "muted" };
    }
    const leadingTeam: 1 | 2 = myTeamWins > opponentsWins ? 1 : 2;
    const lead = Math.max(myTeamWins, opponentsWins);
    const trail = Math.min(myTeamWins, opponentsWins);
    const leaderName = leadingTeam === 1 ? team1DisplayName : team2DisplayName;
    return {
      text: `${leaderName} ${allSetsResolved ? "gana" : "va ganando"} ${lead}-${trail}`,
      tone: leadingTeam === 1 ? "team1" : "team2",
    };
  }, [
    allScoresEntered,
    setsWithWinner,
    myTeamWins,
    opponentsWins,
    team1DisplayName,
    team2DisplayName,
    allSetsResolved,
  ]);

  const canSubmit = isLeagueMatch 
    ? (isAdminMode 
        ? (matchFormat === "singles" ? team1Player1 && opponent1 : team1Player1 && partner && opponent1 && opponent2)
        : (matchFormat === "singles" ? opponent1 : partner && opponent1 && opponent2))
    : partnerPlayer && opponent1Player && opponent2Player;

  const scoreIsComplete = score.every(s => s.myTeam !== null && s.opponents !== null);
  const finalCanSubmit = canSubmit && scoreIsComplete;

  return (
    <Card data-testid="card-match-record" className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 pb-4">
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          Registrar Partido
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {leagues.length > 0 && !fixedLeagueId && (
            <div className="space-y-3">
              {/* Banner de recordatorio para seleccionar liga */}
              {selectedLeagues.length === 0 ? (
                <div 
                  className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
                  data-testid="banner-league-reminder"
                >
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      ¿Quieres que cuente para una liga?
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Selecciona una liga abajo para que este partido afecte su ranking
                    </p>
                  </div>
                </div>
              ) : (
                <div 
                  className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800"
                  data-testid="banner-league-selected"
                >
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    Este partido afectará el ranking de {selectedLeagues.length === 1 ? 'la liga seleccionada' : `${selectedLeagues.length} ligas`}
                  </p>
                </div>
              )}

              <Label className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4 text-muted-foreground" />
                Registrar en Ligas
              </Label>
              
              <div className="space-y-2">
                {leagues.map((league) => {
                  const isSelected = selectedLeagues.includes(league.id);
                  return (
                    <label 
                      key={league.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all select-none",
                        isSelected 
                          ? "border-primary bg-primary/5" 
                          : "border-muted hover:border-muted-foreground/30 hover:bg-muted/30"
                      )}
                      data-testid={`league-option-${league.id}`}
                    >
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleLeague(league.id)}
                        data-testid={`checkbox-league-${league.id}`}
                      />
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-sm font-medium">{league.name}</span>
                      {isSelected && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </label>
                  );
                })}
              </div>

              {selectedLeagues.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {selectedLeagues.map(leagueId => {
                    const league = leagues.find(l => l.id === leagueId);
                    return (
                      <Badge 
                        key={leagueId} 
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        <Trophy className="h-3 w-3" />
                        {league?.name}
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLeague(leagueId);
                          }}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}

              {selectedLeagues.length > 1 && commonMembers.length === 0 && !loadingMembers && (
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  No hay jugadores en común entre las ligas seleccionadas
                </p>
              )}

            </div>
          )}
          
          {fixedLeagueId && leagues.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Trophy className="h-4 w-4 text-muted-foreground" />
                Liga
              </Label>
              <div className="flex items-center gap-2 p-3 rounded-lg border border-primary bg-primary/5">
                <Trophy className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{leagues[0]?.name}</span>
                <Check className="h-4 w-4 text-primary ml-auto" />
              </div>
              {isAdminMode && (
                <p className="text-xs text-muted-foreground">
                  Modo Admin: Puedes registrar partidos sin participar
                </p>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Swords className="h-5 w-5 text-primary" />
              <Label className="text-base font-semibold">Jugadores</Label>
            </div>
            
            {/* Mobile-first vertical layout */}
            <div className="space-y-4">
              {/* Tu Equipo */}
              <div className={cn(
                "p-4 rounded-2xl border-2 transition-all",
                matchWinner === "myTeam" 
                  ? "border-green-500 bg-green-500/10" 
                  : "border-primary/30 bg-primary/5"
              )}>
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-primary/20 text-primary border-0 text-xs font-medium px-3 py-1">
                    Tu Equipo
                  </Badge>
                  {matchWinner === "myTeam" && (
                    <Badge className="bg-green-500 text-white border-0 text-xs">Ganador</Badge>
                  )}
                </div>
                
                <div className={cn(
                  "gap-3",
                  isLeagueMatch ? "grid grid-cols-2" : "space-y-3"
                )}>
                  {/* Team 1 Player 1 - Fixed for normal mode, Selectable for admin mode */}
                  {isAdminMode && isLeagueMatch ? (
                    <SearchableMemberSelect value={team1Player1} onValueChange={setTeam1Player1} members={availableTeam1Player1} disabled={loadingMembers || availableTeam1Player1.length === 0} placeholder="Jugador 1" colorScheme="primary" testId="select-team1-player1" loadingText={loadingMembers ? "Cargando..." : undefined} />
                  ) : (
                    <div className={cn(
                      "flex items-center gap-3 p-3 rounded-xl bg-background/50 border border-primary/20",
                      isLeagueMatch && "flex-col"
                    )}>
                      <Avatar className={cn("border-2 border-primary shadow-md", isLeagueMatch ? "h-12 w-12" : "h-10 w-10")}>
                        <AvatarFallback className="bg-primary text-primary-foreground text-sm font-bold">
                          {getInitials(currentUserName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className={cn(isLeagueMatch && "text-center")}>
                        <span className="text-sm font-medium truncate block">{currentUserName}</span>
                        <span className="text-xs text-muted-foreground">Tú</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Partner - Selectable */}
                  <div className={cn(isLeagueMatch && "flex flex-col items-center")}>
                    {isLeagueMatch ? (
                      <SearchableMemberSelect value={partner} onValueChange={setPartner} members={availablePartners} disabled={loadingMembers || availablePartners.length === 0} placeholder="Compañero" colorScheme="primary" testId="select-partner" loadingText={loadingMembers ? "Cargando..." : undefined} />
                    ) : (
                      <div className="w-full">
                        <PlayerSearchInput
                          value={partnerPlayer}
                          onChange={setPartnerPlayer}
                          placeholder="Buscar compañero..."
                          excludeIds={[currentUserId || "", opponent1Player?.id, opponent2Player?.id].filter(Boolean) as string[]}
                          label="Compañero"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* VS Divider */}
              <div className="flex items-center justify-center py-2">
                <div className="flex-1 h-px bg-border" />
                <div className="px-4">
                  <span className="text-xl font-black text-muted-foreground">VS</span>
                </div>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Rivales */}
              <div className={cn(
                "p-4 rounded-2xl border-2 transition-all",
                matchWinner === "opponents" 
                  ? "border-green-500 bg-green-500/10" 
                  : "border-destructive/30 bg-destructive/5"
              )}>
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-destructive/20 text-destructive border-0 text-xs font-medium px-3 py-1">
                    Rivales
                  </Badge>
                  {matchWinner === "opponents" && (
                    <Badge className="bg-green-500 text-white border-0 text-xs">Ganador</Badge>
                  )}
                </div>
                
                <div className={cn(
                  "gap-3",
                  isLeagueMatch ? "grid grid-cols-2" : "space-y-3"
                )}>
                  {/* Opponent 1 */}
                  <div className={cn(isLeagueMatch && "flex flex-col items-center")}>
                    {isLeagueMatch ? (
                      <SearchableMemberSelect value={opponent1} onValueChange={setOpponent1} members={availableOpponents1} disabled={loadingMembers || availableOpponents1.length === 0} placeholder="Rival 1" colorScheme="destructive" testId="select-opponent1" loadingText={loadingMembers ? "Cargando..." : undefined} />
                    ) : (
                      <div className="w-full">
                        <PlayerSearchInput
                          value={opponent1Player}
                          onChange={setOpponent1Player}
                          placeholder="Buscar rival..."
                          excludeIds={[currentUserId || "", partnerPlayer?.id, opponent2Player?.id].filter(Boolean) as string[]}
                          label="Rival 1"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Opponent 2 */}
                  <div className={cn(isLeagueMatch && "flex flex-col items-center")}>
                    {isLeagueMatch ? (
                      <SearchableMemberSelect value={opponent2} onValueChange={setOpponent2} members={availableOpponents2} disabled={loadingMembers || availableOpponents2.length === 0} placeholder="Rival 2" colorScheme="destructive" testId="select-opponent2" loadingText={loadingMembers ? "Cargando..." : undefined} />
                    ) : (
                      <div className="w-full">
                        <PlayerSearchInput
                          value={opponent2Player}
                          onChange={setOpponent2Player}
                          placeholder="Buscar rival..."
                          excludeIds={[currentUserId || "", partnerPlayer?.id, opponent1Player?.id].filter(Boolean) as string[]}
                          label="Rival 2"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {isLeagueMatch && commonMembers.length < 4 && (
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10">
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  La liga necesita al menos 4 miembros para registrar partidos de dobles
                </p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Trophy className="h-4 w-4 text-primary" />
                Marcador
              </Label>
              {score.length < 3 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addSet}
                  className="h-7 text-xs"
                  data-testid="button-add-set"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Set
                </Button>
              )}
            </div>

            <div className="space-y-2">
              {score.map((set, i) => (
                <div
                  key={i}
                  className={cn(
                    "p-3 rounded-lg border transition-all space-y-2",
                    set.myTeam !== null && set.opponents !== null && set.myTeam > set.opponents
                      ? "bg-green-500/10 border-green-500/30"
                      : set.myTeam !== null && set.opponents !== null && set.opponents > set.myTeam
                        ? "bg-red-500/10 border-red-500/30"
                        : "bg-muted/50 border-muted"
                  )}
                >
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <Badge variant="outline" className="text-xs font-normal shrink-0">
                      Set {i + 1}
                    </Badge>
                    <div className="flex flex-wrap items-center gap-1">
                      {QUICK_RESULTS.map((r) => {
                        const active =
                          set.myTeam === r.myTeam && set.opponents === r.opponents;
                        return (
                          <Button
                            key={`${r.myTeam}-${r.opponents}`}
                            type="button"
                            size="sm"
                            variant={active ? "default" : "outline"}
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              applyQuickResult(i, r.myTeam, r.opponents)
                            }
                            data-testid={`button-quick-${i}-${r.myTeam}-${r.opponents}`}
                          >
                            {r.myTeam}-{r.opponents}
                          </Button>
                        );
                      })}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() =>
                          applyQuickResult(
                            i,
                            set.opponents ?? 0,
                            set.myTeam ?? 0,
                          )
                        }
                        data-testid={`button-invert-${i}`}
                      >
                        Invertir
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                  
                  <div className="flex-1 flex items-center justify-center gap-2">
                    <Select 
                      value={set.myTeam?.toString() ?? ""} 
                      onValueChange={(v) => updateScore(i, "myTeam", parseInt(v))}
                    >
                      <SelectTrigger 
                        className={cn(
                          "w-14 h-10 text-lg font-bold text-center",
                          set.myTeam !== null && set.opponents !== null && set.myTeam > set.opponents && "text-green-600 dark:text-green-400"
                        )}
                        data-testid={`select-score-myteam-${i}`}
                      >
                        <SelectValue placeholder="-">
                          {set.myTeam !== null ? set.myTeam : ""}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_SCORES.map((s) => (
                          <SelectItem key={s} value={s.toString()}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <span className="text-lg font-bold text-muted-foreground">-</span>

                    <Select 
                      value={set.opponents?.toString() ?? ""} 
                      onValueChange={(v) => updateScore(i, "opponents", parseInt(v))}
                    >
                      <SelectTrigger 
                        className={cn(
                          "w-14 h-10 text-lg font-bold text-center",
                          set.myTeam !== null && set.opponents !== null && set.opponents > set.myTeam && "text-green-600 dark:text-green-400"
                        )}
                        data-testid={`select-score-opponents-${i}`}
                      >
                        <SelectValue placeholder="-">
                          {set.opponents !== null ? set.opponents : ""}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_SCORES.map((s) => (
                          <SelectItem key={s} value={s.toString()}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {score.length > 1 && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => removeSet(i)}
                      data-testid={`button-remove-set-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1 text-center">
              <p
                className={cn(
                  "text-sm font-medium",
                  resultPreview.tone === "team1"
                    ? "text-primary"
                    : resultPreview.tone === "team2"
                      ? "text-destructive"
                      : "text-muted-foreground",
                )}
                data-testid="text-record-result-preview"
              >
                {resultPreview.text}
              </p>
              {allScoresEntered && setsWithWinner > 0 && myTeamWins === opponentsWins ? (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="text-record-winner-hint"
                >
                  Empate en sets — el ganador se decide por juegos totales.
                </p>
              ) : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Club (opcional)</Label>
            <ClubPicker
              value={homeClubId}
              onChange={(id) => {
                setClubTouched(true);
                setHomeClubId(id);
              }}
              country={country}
              testId="record-match-club-picker"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
            <div>
              <Label htmlFor="competitive" className="font-medium text-sm">
                Partido Competitivo
              </Label>
              <p className="text-xs text-muted-foreground">
                {selectedLeagues.length > 0
                  ? "Afecta ratings de liga e individual" 
                  : "Afecta tu nivel de habilidad"}
              </p>
            </div>
            <Switch
              id="competitive"
              checked={competitive}
              onCheckedChange={setCompetitive}
              data-testid="switch-competitive"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/25 font-semibold text-base" 
            size="lg"
            disabled={!finalCanSubmit}
            data-testid="button-submit-match"
          >
            <Trophy className="h-5 w-5 mr-2" />
            {selectedLeagues.length > 1 
              ? `Registrar en ${selectedLeagues.length} Ligas`
              : selectedLeagues.length === 1 
                ? "Registrar Partido de Liga" 
                : "Registrar Partido"}
            <ChevronRight className="h-5 w-5 ml-2" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
